// angular
import { CommonModule, NgClass } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  Renderer2,
  viewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'pwj-video-player',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    NgClass,
  ],
  templateUrl: './video-player.component.html',
  styleUrl: './video-player.component.scss',
})
export class VideoPlayerComponent implements AfterViewInit, AfterViewChecked {
  public hiddenPlayBtn: boolean = false;
  public isVideoPlaying: boolean = false;
  public videoDurationInSeconds: number = 0;
  public videoCurrentTimeInSeconds: number = 0;
  public currentSpeed: number = 1;
  public speedOptions: number[] = [0.5, 1, 1.5, 2];
  public currentVolume: number = 1; // Default volume is 100% (1.0)
  public isMuted: boolean = false;

  // inputs
  playBtnText = input<string>('play');
  btnsClass = input<string>();
  videoPreviewSrc = input.required<string>();
  disableBtn = input<boolean>(false);
  isFullWidth = input<boolean>(true);
  playBtnSize = input<'small' | 'large'>('large');

  // view child
  progressRange = viewChild<ElementRef | undefined>('progressRange');
  progressBox = viewChild<ElementRef | undefined>('progressBox');

  volumeRange = viewChild<ElementRef | undefined>('volumeRange');
  volumeBox = viewChild<ElementRef | undefined>('volumeBox');

  videoElement = viewChild.required<ElementRef>('videoElement');
  videoSourceElement = viewChild.required<ElementRef>('videoSourceElement');

  // dependencies
  private readonly _renderer = inject(Renderer2);
  constructor() {}

  ngAfterViewInit() {
    if (this.videoElement()) {
      this.videoElement().nativeElement.addEventListener(
        'play',
        (event: any) => {
          this.isVideoPlaying = true;
        }
      );

      this.videoElement().nativeElement.addEventListener(
        'pause',
        (event: any) => {
          this.hiddenPlayBtn = false;
          this.isVideoPlaying = false;
        }
      );
    }

    if (this.videoElement()) {
      this.hiddenPlayBtn = false;
      this.isVideoPlaying = false;
    }
  }

  ngAfterViewChecked(): void {
    const progressPercent = this._playbackProgressPercent();

    if (this.progressRange() && progressPercent !== null) {
      this._renderer.setStyle(
        this.progressRange()?.nativeElement,
        'width',
        `${progressPercent}%`
      );
    }

    if (this.volumeRange()) {
      // set range left position value
      this._renderer.setStyle(
        this.volumeRange()?.nativeElement,
        'rtl',
        `${this.changeVolume}%`
      );

      // set range width value
      this._renderer.setStyle(
        this.volumeRange()?.nativeElement,
        'width',
        `${this.currentVolume * 100}%`
      );
    }
  }

  // play the video functionality
  public playVideo(videoElement: HTMLVideoElement) {
    if (!this.disableBtn()) {
      videoElement.play();
    }
  }

  // pause the video functionality
  public pauseVideo(videoElement: HTMLVideoElement) {
    if (!this.disableBtn()) {
      videoElement.pause();
    }
  }

  // Function to capture video duration
  public videoDuration(event: Event): void {
    const videoElement = event.target as HTMLVideoElement;
    const duration = videoElement.duration;

    if (Number.isFinite(duration) && duration > 0) {
      this.videoDurationInSeconds = duration;
    }
  }

  // Function to capture current video time
  public videoCurrentTime(event: Event): void {
    const videoElement = event.target as HTMLVideoElement;
    this.videoCurrentTimeInSeconds = videoElement.currentTime;
  }

  // Utility function to format time in mm:ss
  public formatTime(timeInSeconds: number): string {
    if (!Number.isFinite(timeInSeconds) || timeInSeconds < 0) {
      return '00:00';
    }

    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);

    const formattedMinutes = minutes.toString().padStart(2, '0');
    const formattedSeconds = seconds.toString().padStart(2, '0');

    return `${formattedMinutes}:${formattedSeconds}`;
  }

  // Function to handle seeking the video using progress bar
  public seekVideo(event: Event): void {
    const target = event.target as HTMLInputElement;
    const seekTime = parseFloat(target.value);

    // Set the current time of the video to the value selected in the progress bar
    this.videoElement().nativeElement.currentTime = seekTime;
  }

  // Handle clicks on the video progress bar
  public handleVideoProgressBarClick(
    event: MouseEvent,
    progressBox: HTMLElement
  ): void {
    const rect = progressBox.getBoundingClientRect();
    const offsetX = event.clientX - rect.left; // Get the X position of the click
    const totalWidth = rect.width; // Get the total width of the progress box
    const percentage = offsetX / totalWidth; // Calculate the percentage of the click position
    const newTime = percentage * this.videoDurationInSeconds; // Calculate the new video time

    this.videoElement().nativeElement.currentTime = newTime; // Set the video time
    this.videoCurrentTimeInSeconds = newTime; // Update the current time
  }

  // Jump forward 30 seconds
  public nextSeconds(): void {
    const newTime = this.videoElement().nativeElement.currentTime + 15;
    // Ensure the new time does not exceed the video's duration
    this.videoElement().nativeElement.currentTime = Math.min(
      newTime,
      this.videoDurationInSeconds
    );
  }

  // Jump backward 30 seconds
  public prevSeconds(): void {
    const newTime = this.videoElement().nativeElement.currentTime - 15;
    // Ensure the new time does not go below 0
    this.videoElement().nativeElement.currentTime = Math.max(newTime, 0);
  }

  // Toggle Fullscreen Mode
  public toggleFullScreen(): void {
    const videoElement = this.videoElement().nativeElement;

    if (!document.fullscreenElement) {
      // Enter fullscreen
      if (videoElement.requestFullscreen) {
        videoElement.requestFullscreen();
      } else if (videoElement.mozRequestFullScreen) {
        // Firefox
        videoElement.mozRequestFullScreen();
      } else if (videoElement.webkitRequestFullscreen) {
        // Chrome, Safari and Opera
        videoElement.webkitRequestFullscreen();
      } else if (videoElement.msRequestFullscreen) {
        // IE/Edge
        videoElement.msRequestFullscreen();
      }
    }
  }

  // Toggle between speed options
  public toggleSpeed(): void {
    // Cycle through the speed options (0.5x, 1x, 1.5x, 2x)
    const currentIndex = this.speedOptions.indexOf(this.currentSpeed);
    const nextIndex = (currentIndex + 1) % this.speedOptions.length;
    this.currentSpeed = this.speedOptions[nextIndex];

    // Apply the speed to the video
    this.videoElement().nativeElement.playbackRate = this.currentSpeed;
  }

  // update volume
  public changeVolume(event: Event): void {
    const target = event.target as HTMLInputElement;
    const volume = parseFloat(target.value);

    this.setVolume(volume);
  }

  // Toggle mute/unmute
  public toggleMute(): void {
    if (this.isMuted) {
      this.setVolume(1);
    } else {
      this.setVolume(0);
    }
  }

  // set video volume
  public setVolume(newVolume: number) {
    this.currentVolume = newVolume;

    this.videoElement().nativeElement.volume = this.currentVolume;

    // Update mute state based on volume
    if (this.currentVolume === 0) {
      this.isMuted = true;
    } else {
      this.isMuted = false;
    }
  }

  // Handle clicks on the video progress bar
  public handleSoundProgressBarClick(
    event: MouseEvent,
    volumeBox: HTMLElement
  ): void {
    const rect = volumeBox.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const totalWidth = rect.width;
    const percentage = offsetX / totalWidth;
    const newVolume = Math.min(Math.max(percentage, 0), 1); // Ensure volume is between 0 and 1

    this.currentVolume = newVolume;
    this.videoElement().nativeElement.volume = this.currentVolume;
    this.isMuted = this.currentVolume === 0;
  }

  // show video controllers functionality
  public showControllers() {
    this.hiddenPlayBtn = false;
  }

  @HostListener('mouseout', ['$event'])
  // detect when click out of the menu
  onMouseOut(event: MouseEvent) {
    const target = event.relatedTarget as HTMLElement;

    if (target && !this.isInside(event.currentTarget as HTMLElement, target)) {
      this.hiddenPlayBtn = true;
    }
  }

  public isInside(parent: HTMLElement, target: HTMLElement): boolean {
    return parent.contains(target);
  }

  private _playbackProgressPercent(): number | null {
    if (
      !Number.isFinite(this.videoDurationInSeconds) ||
      this.videoDurationInSeconds <= 0
    ) {
      return null;
    }

    return Math.min(
      100,
      (this.videoCurrentTimeInSeconds / this.videoDurationInSeconds) * 100
    );
  }
}
